import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Output,
    computed,
    forwardRef,
    input,
    signal
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TagComponent, TagOption } from "./tag.component";

@Component({
    selector: "app-tag-list",
    templateUrl: "./tag-list.component.html",
    styleUrl: "./tag-list.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TagComponent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TagListComponent),
            multi: true
        }
    ]
})
export class TagListComponent<T = unknown> implements ControlValueAccessor {
    /**
     * When true, hides the remove button on all tags.
     * Also set automatically by Angular forms when the bound control is disabled.
     */
    disabled = input<boolean>(false);

    /** Emits the option of whichever tag label was clicked. */
    @Output() clicked = new EventEmitter<TagOption<T>>();
    /** Emits the option of whichever tag was removed. */
    @Output() removed = new EventEmitter<TagOption<T>>();

    tags = signal<TagOption<T>[]>([]);
    private readonly disabledFromCva = signal(false);

    isDisabled = computed(() => this.disabled() || this.disabledFromCva());

    private onChange: (value: TagOption<T>[]) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(value: TagOption<T>[] | null): void {
        this.tags.set(value ?? []);
    }

    registerOnChange(fn: (value: TagOption<T>[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabledFromCva.set(isDisabled);
    }

    onRemove(tag: TagOption<T>): void {
        const updated = this.tags().filter(t => t.value !== tag.value);
        this.tags.set(updated);
        this.onChange(updated);
        this.onTouched();
        this.removed.emit(tag);
    }
}
